import React, {
  useEffect,
  useReducer,
  Fragment,
  useRef,
  useState,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import classNames from "classnames";
import { Link, useHistory } from "react-router-dom";

import { selectToken, selectCurrentUser } from "../../redux/user/userSelectors";

import { showModal, hideModal } from "../../redux/modal/modalActions";
import { showAlert } from "../../redux/alert/alertActions";

import { getPost, deletePost } from "../../services/postService";
import { getComments } from "../../services/commentService";

import Avatar from "@mui/material/Avatar";
import Icon from "../Icon/Icon";
import SkeletonLoader from "../SkeletonLoader/SkeletonLoader";
import Comment from "../Comment/Comment";
import PostDialogCommentForm from "./PostDialogCommentForm/PostDialogCommentForm";
import PostDialogStats from "./PostDialogStats/PostDialogStats";
import { normalizeMediaType } from "../../utils/media";
import { subscribeToTier, purchasePost } from "../../services/monetizationService";

import { INITIAL_STATE, postDialogReducer } from "./postDialogReducer";

const TRANSIENT_CONTROLS_TIMEOUT = 3000;

const PostDialog = ({
  postId,
  token,
  currentUser,
  profileDispatch,
  showModal,
  hideModal,
  showAlert,
  style,
  className,
  postData,
  loading,
  simple,
  hideComments = false,
  onCommentClick,
  immersive = false,
  isActive = false,
  autoPlay = false,
}) => {
  const commentsRef = useRef();
  const videoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [manualPlaybackRequired, setManualPlaybackRequired] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [transientControlsVisible, setTransientControlsVisible] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [state, dispatch] = useReducer(postDialogReducer, INITIAL_STATE);
  const monetization = state.data?.monetization || {};
  const resolvedMonetizationType =
    typeof monetization?.monetizationType === "number"
      ? ["Free", "SubscribersOnly", "PayPerView"][monetization.monetizationType] || "Free"
      : monetization?.monetizationType;
  const isLocked = Boolean(monetization?.isLocked);
  const formatCurrency = useCallback(
    (amountCents, currency = monetization?.currency || "AUD") => {
      if (typeof amountCents !== "number") {
        return "";
      }
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency,
          maximumFractionDigits: 2,
        }).format(amountCents / 100);
      } catch {
        return `$${(amountCents / 100).toFixed(2)}`;
      }
    },
    [monetization?.currency]
  );
  const refreshPost = useCallback(async () => {
    if (!postId) {
      return;
    }
    try {
      const refreshed = await getPost(postId, token);
      dispatch({
        type: "FETCH_POST_SUCCESS",
        payload: {
          ...refreshed,
          postVotes: Array.isArray(refreshed.postVotes)
            ? [...refreshed.postVotes]
            : [],
        },
      });
    } catch (error) {
      console.warn("Unable to refresh post after unlock.", error);
    }
  }, [postId, token]);
  const subscriptionPriceLabel = monetization?.subscriptionPriceCents
    ? `${formatCurrency(monetization.subscriptionPriceCents)} / mo`
    : null;
  const payPerViewPriceLabel = monetization?.payPerViewPriceCents
    ? formatCurrency(monetization.payPerViewPriceCents)
    : null;
  const parseMetadata = (metadata) => {
    if (!metadata) {
      return null;
    }
    if (typeof metadata === "object") {
      return metadata;
    }
    try {
      return JSON.parse(metadata);
    } catch (err) {
      return null;
    }
  };

  const getMediaDetails = () => {
    if (isLocked) {
      return {
        mediaUrl: "",
        thumbnail: "",
        metadata: null,
        mediaType: "image",
      };
    }
    const mediaItem = state.data.mediaItems?.[0];
    const directUrl = state.data.mediaUrl || state.data.image || mediaItem?.mediaUrl || "";
    const thumbnail =
      mediaItem?.thumbnailUrl ||
      state.data.thumbnail ||
      state.data.image ||
      directUrl;
    const metadata = parseMetadata(mediaItem?.metadata || state.data.metadata);
    const mediaType = normalizeMediaType(
      mediaItem?.mediaType || state.data.mediaType,
      directUrl
    );
    return {
      mediaUrl: mediaItem?.mediaUrl || directUrl,
      thumbnail,
      metadata,
      mediaType,
    };
  };
  const history = useHistory();

  const fetching = loading !== undefined ? loading : state.fetching;
  const showInlineComments = !hideComments || commentsOpen;
  const handleSubscribe = useCallback(async () => {
    if (!token) {
      showAlert("Sign in to subscribe.");
      return;
    }
    if (!monetization?.subscriptionTierId) {
      showAlert("This post is not linked to an active tier.");
      return;
    }
    try {
      setUnlocking(true);
      await subscribeToTier(monetization.subscriptionTierId, token, true);
      showAlert("Subscription activated. Enjoy the premium post!");
      await refreshPost();
    } catch (error) {
      showAlert(error?.message || "Unable to subscribe right now.");
    } finally {
      setUnlocking(false);
    }
  }, [monetization?.subscriptionTierId, refreshPost, showAlert, token]);

  const handlePurchase = useCallback(async () => {
    if (!token) {
      showAlert("Sign in to unlock this post.");
      return;
    }
    if (!postId) {
      return;
    }
    try {
      setUnlocking(true);
      await purchasePost(postId, token);
      showAlert("Post unlocked!");
      await refreshPost();
    } catch (error) {
      showAlert(error?.message || "Unable to process your unlock.");
    } finally {
      setUnlocking(false);
    }
  }, [postId, refreshPost, showAlert, token]);

  useEffect(() => {
    if (!loading) {
      // Check if the post data is already provided by another component
      if (postData) {
        const sanitizedPostData = {
          ...postData,
          postVotes: Array.isArray(postData.postVotes)
            ? [...postData.postVotes]
            : [],
        };
        dispatch({ type: "FETCH_POST_SUCCESS", payload: sanitizedPostData });
      } else {
        window.history.pushState(
          { prevUrl: window.location.href },
          null,
          `/post/${postId}`
        );
        (async function () {
          try {
            const response = await getPost(postId, token);
            const sanitizedResponse = {
              ...response,
              postVotes: Array.isArray(response.postVotes)
                ? [...response.postVotes]
                : [],
            };
            dispatch({ type: "FETCH_POST_SUCCESS", payload: sanitizedResponse });
          } catch (err) {
            history.push("/");
            dispatch({ type: "FETCH_POST_FAILURE", payload: err });
          }
        })();
      }
    }

    return () => {
      if (window.history.state && window.history.state.prevUrl) {
        window.history.pushState(
          "profile",
          "Profile",
          window.history.state.prevUrl
        );
      }
    };
  }, [postId, history, loading, postData, token]);

  useEffect(() => {
    if (commentsOpen && commentsRef.current) {
      commentsRef.current.scrollTop = commentsRef.current.scrollHeight;
    }
  }, [commentsOpen, state.data.comments.length]);

  const fetchAdditionalComments = async () => {
    try {
      const commentData = await getComments(
        postId,
        state.data.comments.length,
        state.localStateComments.size
      );
      dispatch({ type: "ADD_COMMENT", payload: commentData.comments });
      if (typeof commentData.totalCount === 'number') {
        dispatch({ type: "SET_COMMENTS_TOTAL", payload: commentData.totalCount });
      }
    } catch (err) {
      showAlert("Unable to fetch additional comments.", () =>
        fetchAdditionalComments()
      );
    }
  };

  const handleDeletePost = async () => {
    try {
      await deletePost(postId, token);
      profileDispatch &&
        profileDispatch({
          type: "DELETE_POST",
          payload: postId,
        });
      hideModal("PostDialog/PostDialog");
    } catch (err) {
      showAlert("Unable to delete post.", () => handleDeletePost());
    }
  };

  const mediaDetails = getMediaDetails();

  const enableAutoPlayback = autoPlay;
  const forceVideoControls = !enableAutoPlayback || manualPlaybackRequired;
  const shouldShowVideoControls =
    forceVideoControls || transientControlsVisible;
  const immersiveHasControls = immersive && shouldShowVideoControls;
  const shouldAttachVideoRef =
    autoPlay || immersive || shouldShowVideoControls;

  const videoControlAttributes = shouldShowVideoControls ? { controls: true } : {};

  const clearControlsTimeout = useCallback(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = null;
    }
  }, []);

  const scheduleControlsHide = useCallback(() => {
    clearControlsTimeout();
    controlsTimeoutRef.current = setTimeout(() => {
      setTransientControlsVisible(false);
      controlsTimeoutRef.current = null;
    }, TRANSIENT_CONTROLS_TIMEOUT);
  }, [clearControlsTimeout]);

  const revealTransientControls = useCallback(() => {
    if (forceVideoControls) {
      return;
    }
    setTransientControlsVisible(true);
    scheduleControlsHide();
  }, [forceVideoControls, scheduleControlsHide]);

  const handleVideoContextMenu = useCallback((event) => {
    event.preventDefault();
  }, []);

  useEffect(() => {
    setVideoError(false);
  }, [mediaDetails.mediaUrl]);

  useEffect(() => {
    setManualPlaybackRequired(false);
  }, [autoPlay, mediaDetails.mediaUrl]);

  const handleTogglePlayback = useCallback(() => {
    revealTransientControls();
    const node = videoRef.current;
    if (!node) {
      return;
    }
    if (node.paused || node.ended) {
      const playPromise = node.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => setManualPlaybackRequired(false))
          .catch(() => setManualPlaybackRequired(true));
      } else {
        setManualPlaybackRequired(false);
      }
    } else {
      node.pause();
    }
  }, [revealTransientControls]);

  const handleVideoTouchEnd = useCallback(
    (event) => {
      if (event.currentTarget !== event.target) {
        return;
      }
      handleTogglePlayback();
    },
    [handleTogglePlayback]
  );

  const handleVideoKeyDown = useCallback(
    (event) => {
      if (event.key !== " " && event.key !== "Enter") {
        return;
      }
      event.preventDefault();
      handleTogglePlayback();
    },
    [handleTogglePlayback]
  );

  const handleVideoMouseUp = useCallback(
    (event) => {
      if (event.currentTarget !== event.target) {
        return;
      }
      if (event.button !== 0) {
        return;
      }
      handleTogglePlayback();
    },
    [handleTogglePlayback]
  );

  useEffect(() => {
    const node = videoRef.current;
    if (!node) {
      return;
    }
    if (!shouldShowVideoControls) {
      node.removeAttribute("controlsList");
      return;
    }
    try {
      if (node.controlsList && typeof node.controlsList.remove === "function") {
        try {
          node.controlsList.remove("download");
        } catch (err) {
          // ignore inability to remove nonexistent token
        }
        if (typeof node.controlsList.add === "function") {
          try {
            node.controlsList.add("nodownload");
            return;
          } catch (err) {
            // fall through to attribute assignment
          }
        }
      }
      node.setAttribute("controlsList", "nodownload");
    } catch (err) {
      node.setAttribute("controlsList", "nodownload");
    }
  }, [shouldShowVideoControls, mediaDetails.mediaUrl]);

  const attemptPlayback = useCallback(() => {
    if (
      !enableAutoPlayback ||
      manualPlaybackRequired ||
      mediaDetails.mediaType !== "video"
    ) {
      return;
    }
    const node = videoRef.current;
    if (!node) {
      return;
    }
    const playPromise = node.play();
    if (playPromise && typeof playPromise.then === "function") {
      playPromise.catch(() => {
        setManualPlaybackRequired(true);
      });
    }
  }, [
    enableAutoPlayback,
    manualPlaybackRequired,
    mediaDetails.mediaType,
    mediaDetails.mediaUrl,
  ]);

  useEffect(() => {
    if (!autoPlay || mediaDetails.mediaType !== "video") {
      return;
    }
    const node = videoRef.current;
    if (!node) {
      return;
    }
    if (isActive) {
      attemptPlayback();
    } else {
      node.pause();
      try {
        node.currentTime = 0;
      } catch (err) {
        // ignore inability to reset currentTime
      }
    }
  }, [autoPlay, isActive, mediaDetails.mediaType, mediaDetails.mediaUrl, attemptPlayback]);

  useEffect(() => {
    if (!isActive && commentsOpen) {
      setCommentsOpen(false);
    }
  }, [isActive, commentsOpen]);

  useEffect(() => {
    if (!isActive) {
      setTransientControlsVisible(false);
      clearControlsTimeout();
    }
  }, [isActive, clearControlsTimeout]);

  useEffect(() => {
    if (forceVideoControls) {
      setTransientControlsVisible(false);
      clearControlsTimeout();
    }
  }, [forceVideoControls, clearControlsTimeout]);

  useEffect(
    () => () => {
      clearControlsTimeout();
    },
    [clearControlsTimeout]
  );

  return (
    <div
      className={classNames({
        "post-dialog": true,
        "post-dialog--simple": simple,
        "post-dialog--immersive": immersive,
        "post-dialog--immersive-controls": immersiveHasControls,
        [className]: className,
      })}
      data-test="component-post-dialog"
      style={style}
    >
      <Fragment>
        <div
          className={classNames({
            "post-dialog__image": true,
            "post-dialog__image--simple": simple,
            "post-dialog__image--locked": isLocked,
          })}
        >
          {fetching ? (
            <SkeletonLoader animated />
          ) : mediaDetails.mediaType === "video" && !videoError && !isLocked ? (
            <video
              ref={shouldAttachVideoRef ? videoRef : null}
              src={mediaDetails.mediaUrl}
              poster={mediaDetails.thumbnail}
              {...videoControlAttributes}
              onContextMenu={handleVideoContextMenu}
              onMouseUp={handleVideoMouseUp}
              onTouchEnd={handleVideoTouchEnd}
              onKeyDown={handleVideoKeyDown}
              tabIndex={0}
              aria-label="Toggle video playback"
              muted={enableAutoPlayback && !manualPlaybackRequired}
              defaultMuted={enableAutoPlayback}
              loop={enableAutoPlayback}
              autoPlay={enableAutoPlayback && !manualPlaybackRequired}
              preload={
                enableAutoPlayback && !manualPlaybackRequired
                  ? "auto"
                  : "metadata"
              }
              playsInline
              onLoadedData={() => {
                if (
                  enableAutoPlayback &&
                  isActive &&
                  !manualPlaybackRequired
                ) {
                  attemptPlayback();
                }
              }}
              onError={() => {
                setVideoError(true);
              }}
              style={{ width: '100%', height: '100%' }}
            />
          ) : isLocked ? null : (
            <img
              src={mediaDetails.thumbnail || mediaDetails.mediaUrl}
              alt="Post"
              style={{ filter: state.data.filter }}
            />
          )}
          {!fetching && isLocked && (
            <div className="post-dialog__locked">
              <Icon
                icon="lock-closed"
                className="post-dialog__locked-icon"
              />
              <p className="post-dialog__locked-text">
                {resolvedMonetizationType === "SubscribersOnly"
                  ? `Subscribe to ${
                      monetization?.subscriptionTierName || "this creator"
                    }${
                      subscriptionPriceLabel ? ` · ${subscriptionPriceLabel}` : ""
                    }`
                  : `Unlock this post${
                      payPerViewPriceLabel ? ` · ${payPerViewPriceLabel}` : ""
                    }`}
              </p>
              <div className="post-dialog__locked-actions">
                {resolvedMonetizationType === "SubscribersOnly" && (
                  <button
                    type="button"
                    className="post-dialog__locked-button"
                    onClick={handleSubscribe}
                    disabled={unlocking}
                  >
                    {unlocking
                      ? "Processing…"
                      : subscriptionPriceLabel
                      ? `Subscribe · ${subscriptionPriceLabel}`
                      : "Subscribe"}
                  </button>
                )}
                {resolvedMonetizationType === "PayPerView" && (
                  <button
                    type="button"
                    className="post-dialog__locked-button"
                    onClick={handlePurchase}
                    disabled={unlocking}
                  >
                    {unlocking
                      ? "Processing…"
                      : payPerViewPriceLabel
                      ? `Unlock · ${payPerViewPriceLabel}`
                      : "Unlock"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        <header
          className={classNames({
            "post-dialog__header": true,
            "post-dialog__header--simple": simple,
          })}
        >
          {fetching ? (
            <SkeletonLoader
              style={{ height: "4rem", width: "4rem", borderRadius: "100px" }}
            />
          ) : (
            <Link
              to={`/${state.data.author.userName}`}
              style={{ display: "flex" }}
            >
              <Avatar
                className="avatar--small"
                src={state.data.author.avatar}
              />
            </Link>
          )}
          {fetching ? (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <SkeletonLoader style={{ height: "1rem", width: "10rem" }} />
              <SkeletonLoader
                style={{ height: "1rem", width: "15rem", marginTop: "5px" }}
              />
            </div>
          ) : (
            <Link
              style={{ textDecoration: "none" }}
              to={`/${state.data.author.userName}`}
            >
              <p className="heading-4 heading-4--bold">
                <b>{state.data.author.userName}</b>
              </p>
            </Link>
          )}
          {!fetching && (
            <div
              onClick={() => {
                const options = [
                  {
                    text: "Go to post",
                    onClick: () => {
                      hideModal("PostDialog/PostDialog");
                      history.push(`/post/${postId}`);
                    },
                  },
                  {
                    text: "Copy link",
                    onClick: () => {
                      navigator.clipboard
                        .writeText(document.URL)
                        .then(() => showAlert("Link copied to clipboard."))
                        .catch(() =>
                          showAlert("Could not copy link to clipboard.")
                        );
                    },
                  },
                ];
                showModal(
                  {
                    options:
                      currentUser &&
                      // currentUser.username === state.data.author.username
                      currentUser.username === state.data.author.userName
                        ? [
                            ...options,
                            {
                              text: "Delete post",
                              warning: true,
                              onClick: () => {
                                handleDeletePost();
                                history.push("/" + currentUser.username);
                              },
                            },
                          ]
                        : options,
                  },
                  "OptionsDialog/OptionsDialog"
                );
              }}
              style={{ cursor: "pointer" }}
              className="post-dialog__more"
            >
              <Icon className="icon--small" icon="ellipsis-horizontal" />
            </div>
          )}
        </header>
        <div
          data-test="component-post-dialog-content"
          className={classNames({
            "post-dialog__content": true,
            "post-dialog__content--simple": simple,
            "post-dialog__content--expanded": hideComments && commentsOpen,
          })}
        >
          {showInlineComments ? (
            <div
              ref={commentsRef}
              className={classNames({
                comments: true,
                "comments--simple": simple,
                "comments--immersive": hideComments && !commentsOpen,
              })}
            >
              {/* Render a caption if there is one as a Comment component with the caption prop */}
              {state.data.caption && !fetching ? (
                <Comment
                  comment={{
                    message: state.data.caption,
                    avatar: state.data.author.avatar,
                    username: state.data.author.userName,
                  }}
                  currentUser={currentUser}
                  token={token}
                  post={state.data}
                  caption
                  simple={simple}
                />
              ) : null}
              {!fetching &&
                state.data.comments.map((comment, idx) => (
                  <Comment
                    comment={comment}
                    currentUser={currentUser}
                    token={token}
                    post={state.data}
                    key={idx}
                    dialogDispatch={dispatch}
                    profileDispatch={profileDispatch}
                    simple={simple}
                  />
                ))}
              {!postData &&
                state.data.comments.length - state.localStateComments.size <
                  state.data.commentCount - state.localStateComments.size && (
                  <div
                    style={{ padding: "2rem", cursor: "pointer" }}
                    onClick={() => fetchAdditionalComments()}
                  >
                    <Icon
                      style={{
                        margin: "0 auto",
                      }}
                      icon="add-circle-outline"
                    />
                  </div>
                )}
            </div>
          ) : null}
          {fetching ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                padding: "2rem 2rem 6rem 2rem",
              }}
            >
              <SkeletonLoader style={{ height: "1.5rem", width: "15rem" }} />
              <SkeletonLoader
                style={{ height: "1.5rem", width: "20rem", marginTop: "1rem" }}
              />
              <SkeletonLoader
                style={{ height: "1.5rem", width: "10rem", marginTop: "1rem" }}
              />
            </div>
          ) : (
            <PostDialogStats
              currentUser={currentUser}
              token={token}
              post={state.data}
              dispatch={dispatch}
              profileDispatch={profileDispatch}
              simple={simple}
              onCommentClick={
                hideComments
                  ? () => setCommentsOpen((previous) => !previous)
                  : onCommentClick
              }
              postNote={
                hideComments && !commentsOpen && !fetching && state.data.caption
                  ? state.data.caption
                  : null
              }
              isLocked={isLocked}
            />
          )}
          {!fetching && showInlineComments && !isLocked && (
            <PostDialogCommentForm
              postId={postId}
              token={token}
              currentUser={currentUser}
              commentsRef={commentsRef}
              dialogDispatch={dispatch}
              profileDispatch={profileDispatch}
              replying={state.replying}
            />
          )}
        </div>
      </Fragment>
    </div>
  );
};

PostDialog.propTypes = {
  postId: PropTypes.string,
  token: PropTypes.string,
  currentUser: PropTypes.object,
  profileDispatch: PropTypes.func,
  hideComments: PropTypes.bool,
  onCommentClick: PropTypes.func,
  immersive: PropTypes.bool,
  isActive: PropTypes.bool,
  autoPlay: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  token: selectToken,
  currentUser: selectCurrentUser,
});

const mapDispatchToProps = (dispatch) => ({
  showModal: (props, component) => dispatch(showModal(props, component)),
  hideModal: (component) => dispatch(hideModal(component)),
  showAlert: (text, onClick) => dispatch(showAlert(text, onClick)),
});

PostDialog.whyDidYouRender = true;

export default connect(mapStateToProps, mapDispatchToProps)(PostDialog);
